import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../contexts/AuthContext";
import { PendingChip } from "../../components/PendingChip";
import { getCurrentMonthFirstDay } from "../../utils/calendarHelpers";
import { AdvertiserCreditsSection } from "./AdvertiserCreditsSection";
import { fetchMTDSpend } from "../../services/spendService";
import { isAtOrAboveCapThreshold, checkActiveCreative, dispatchActivationJobs, setCampaignStatus } from "../../services/advertiserActivation";
import { resolveBillingPause, recordOfflinePayment } from "../../services/stripeCardsService";
import { amountLabel } from "../billing/paymentFormatting";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

const KNOWN_GEO_TYPES = [
  { value: "zip_code", label: "Zip Code Targeting" },
];

// Diff field definitions for the pending changes section
const PENDING_DIFF_FIELDS = [
  { key: "name",              label: "Company Name" },
  { key: "website",           label: "Website" },
  { key: "category",          label: "Category" },
  { key: "brand_context",     label: "Context" },
  { key: "brand_suitability", label: "Brand Suitability" },
];

const ADVERTISER_SELECT = "enabled_geo_types, status, name, website, category, uuid, budget_limit, target_margin, billing_method, brand_context, brand_suitability";

// "Cold" account states — never delivered, and never previously delivered from
// this state. Moving OUT of one into active/paused runs the same activation
// prerequisite checks and attribution-threshold bootstrap. `onboarding` is the
// creation default; `canceled` is a wound-down account.
const COLD_STATUSES = ["canceled", "onboarding"];

// How far back the offline-payment picker looks for failed charge attempts,
// anchored on the pause itself. A month-to-date window was wrong: the pause
// persists across month boundaries and the daily job stops producing new
// `failed` rows once it is paused, so an advertiser paused on Aug 30 had an
// empty dropdown (and a permanently disabled "Record payment" button) for
// the whole of September. 90 days always contains the 3 consecutive failures
// that caused the current pause while staying bounded.
const FAILED_ATTEMPT_LOOKBACK_DAYS = 90;

// charge_date is a UTC date column, so the boundary must be built in UTC
// too — getCurrentMonthFirstDay() derives its string from LOCAL time, which
// silently shifts the cutoff by a day either side of midnight for anyone
// west of UTC.
function failedAttemptLookbackStart(pausedAtIso) {
  const anchor = pausedAtIso ? new Date(pausedAtIso) : new Date();
  const base = Number.isNaN(anchor.getTime()) ? new Date() : anchor;
  const start = new Date(base.getTime() - FAILED_ATTEMPT_LOOKBACK_DAYS * 24 * 60 * 60 * 1000);
  return start.toISOString().slice(0, 10);
}

function formatPausedAt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return iso;
  }
}

// Ensures an attribution_score_threshold row exists for the advertiser, creating one with the
// default threshold if missing. Returns an error message string, or null on success.
async function ensureAttributionThreshold(advertiserId) {
  const { data: threshold, error: lookupError } = await supabase
    .from("attribution_score_threshold")
    .select("id")
    .eq("advertiser_id", advertiserId)
    .maybeSingle();
  if (lookupError) return `failed to check attribution threshold: ${lookupError.message}`;
  if (threshold) return null;

  const { error: insertError } = await supabase
    .from("attribution_score_threshold")
    .insert({ advertiser_id: advertiserId, threshold: 49 });
  if (insertError) return `failed to create attribution threshold: ${insertError.message}`;
  return null;
}

export function AdvertiserSettingsModal({ advertiserId, advertiserName, onClose, onSaved }) {
  const { canManageAdvertiserData, isSupportUser } = useAuth();
  const [enabledGeoTypes, setEnabledGeoTypes] = useState([]);
  const [status, setStatus] = useState("onboarding");
  const [budgetLimit, setBudgetLimit] = useState("");
  const [targetMargin, setTargetMargin] = useState("80");
  const [billingMethod, setBillingMethod] = useState(null);
  const [autoChargeEnabled, setAutoChargeEnabled] = useState(false);
  const [autoChargePausedInfo, setAutoChargePausedInfo] = useState(null); // { paused_at, failure_count, paused_reason } | null
  const [autoChargeLoadFailed, setAutoChargeLoadFailed] = useState(false);
  const [advertiserBaseline, setAdvertiserBaseline] = useState(null);
  const initialStatus = useRef("onboarding");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const motion = useModalMotion(onClose, { dismissible: !saving, closeOnBackdrop: true });
  const { requestClose } = motion;
  const [saveError, setSaveError] = useState(null);
  const [loadError, setLoadError] = useState(null);

  // Pending changes state
  const [pendingRow, setPendingRow] = useState(null);
  const [pendingActionError, setPendingActionError] = useState(null);
  const [pendingActioning, setPendingActioning] = useState(false);

  // WU-5 (DEV-2116) — support-only billing-pause resolution panel state.
  const [failedAttempts, setFailedAttempts] = useState(null); // null = not yet fetched
  const [failedAttemptsError, setFailedAttemptsError] = useState(null);
  const [showOfflinePaymentForm, setShowOfflinePaymentForm] = useState(false);
  const [selectedFailedPaymentId, setSelectedFailedPaymentId] = useState("");
  const [offlinePaymentAmount, setOfflinePaymentAmount] = useState("");
  const [offlinePaymentNote, setOfflinePaymentNote] = useState("");
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState(null);
  const [recordResult, setRecordResult] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState(null);
  const legacyCampaignSettingsUrl = advertiserBaseline?.uuid
    ? `https://app.audiohook.com/${advertiserBaseline.uuid}/campaign`
    : null;

  // Fields that ACTUALLY differ from the advertiser's current values. The client
  // submits all five tracked fields on every save, so a pending row always carries
  // a non-null value for every field the proposer left alone — filtering on
  // "is not null" alone would list the whole set as changed and misrepresent the
  // scope of what support is approving.
  const pendingClearedFields = pendingRow?.cleared_fields || [];
  const pendingDiffs = pendingRow
    ? PENDING_DIFF_FIELDS.filter((f) => {
        const current = advertiserBaseline?.[f.key] ?? null;
        // An explicit clear only counts when there was something to clear.
        if (pendingClearedFields.includes(f.key)) return current != null;
        const proposed = pendingRow[f.key];
        if (proposed == null) return false; // not part of this proposal
        return proposed !== current;
      })
    : [];

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setEnabledGeoTypes([]);
    setPendingRow(null);
    setPendingActionError(null);
    if (!supabase || advertiserId == null) { setLoading(false); return; }
    (async () => {
      const [advRes, pendingRes, autoChargeRes] = await Promise.all([
        supabase
          .from("advertisers")
          .select(ADVERTISER_SELECT)
          .eq("id", advertiserId)
          .maybeSingle(),
        supabase
          .from("advertiser_settings_changes")
          .select("id, name, website, category, brand_context, brand_suitability, cleared_fields")
          .eq("advertiser_id", advertiserId)
          .eq("status", "pending")
          .maybeSingle(),
        supabase
          .from("advertiser_auto_charge_settings")
          .select("enabled, paused_reason, paused_at, failure_count")
          .eq("advertiser_id", advertiserId)
          .maybeSingle(),
      ]);
      if (cancelled) return;

      const { data, error } = advRes;
      if (error || !data) {
        setLoadError(error?.message || "Failed to load advertiser settings.");
        setLoading(false);
        return;
      }
      setEnabledGeoTypes(Array.isArray(data?.enabled_geo_types) ? data.enabled_geo_types : []);
      const st = data?.status ?? "onboarding";
      setStatus(st);
      initialStatus.current = st;
      setBudgetLimit(data?.budget_limit != null ? String(data.budget_limit) : "");
      setTargetMargin(data?.target_margin != null ? String(parseFloat((data.target_margin * 100).toFixed(4))) : "80");
      setBillingMethod(data?.billing_method ?? null);
      setAdvertiserBaseline(data);

      // No row means the advertiser is not enrolled in auto-charging.
      // A failed read is not the same as "no row" — record it so Save does
      // not overwrite an unknown value with the default.
      setAutoChargeLoadFailed(Boolean(autoChargeRes?.error));
      const autoCharge = autoChargeRes?.data ?? null;
      setAutoChargeEnabled(autoCharge?.enabled ?? false);
      setAutoChargePausedInfo(
        autoCharge?.paused_reason
          ? { paused_at: autoCharge.paused_at, failure_count: autoCharge.failure_count, paused_reason: autoCharge.paused_reason }
          : null
      );

      if (!pendingRes.error && pendingRes.data) {
        setPendingRow(pendingRes.data);
      }

      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [advertiserId]);

  const handleApprove = async () => {
    if (!supabase || !pendingRow) return;
    setPendingActioning(true);
    setPendingActionError(null);
    const { error } = await supabase.rpc("approve_advertiser_settings_change", { change_id: pendingRow.id });
    setPendingActioning(false);
    if (error) {
      setPendingActionError(error.message || "Failed to approve changes.");
      return;
    }

    // Map brand_suitability when the pending row has a non-null note that
    // differs from the current advertiser baseline. Empty string is valid (note
    // cleared → edge function resets to all-zero default). Comparing against the
    // baseline avoids a redundant LLM call when the approval touches other fields
    // and brand_suitability happens to be included but unchanged.
    // An explicit clear (cleared_fields) has brand_suitability = null, which the
    // plain `pendingRow.brand_suitability` read below is indistinguishable from
    // "not part of this proposal" — treat it as an empty-string note instead so
    // the invoke still fires and the mapping doesn't go stale. This must stay in
    // agreement with UserMenu's support direct-write path (handleSettingsSave),
    // which already invokes with expected_note: "" when the note is cleared.
    const isClearedNote = (pendingRow.cleared_fields || []).includes("brand_suitability");
    const pendingNote = isClearedNote ? "" : pendingRow.brand_suitability;
    const baselineNote = advertiserBaseline?.brand_suitability ?? "";
    if (pendingNote != null && pendingNote.trim() !== baselineNote.trim()) {
      supabase.functions.invoke("map-brand-suitability", {
        body: {
          advertiser_id: advertiserId,
          expected_note: pendingNote.trim(),
        },
      }).then(({ error: invokeError }) => {
        if (invokeError) console.warn("map-brand-suitability invoke failed:", invokeError);
      }).catch((err) => console.warn("map-brand-suitability invoke failed:", err));
    }

    setPendingRow(null);
    // Re-fetch the updated advertiser baseline
    const { data } = await supabase
      .from("advertisers")
      .select(ADVERTISER_SELECT)
      .eq("id", advertiserId)
      .maybeSingle();
    if (data) setAdvertiserBaseline(data);
    onSaved?.();
    requestClose();
  };

  const handleReject = async () => {
    if (!supabase || !pendingRow) return;
    setPendingActioning(true);
    setPendingActionError(null);
    const { error } = await supabase.rpc("reject_advertiser_settings_change", { change_id: pendingRow.id });
    setPendingActioning(false);
    if (error) {
      setPendingActionError(error.message || "Failed to reject changes.");
      return;
    }
    setPendingRow(null);
    onSaved?.();
    requestClose();
  };

  const toggleGeoType = (value) => {
    setEnabledGeoTypes(prev => prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]);
  };

  // WU-5 (DEV-2116) — the modal doesn't call stripeCardsService anywhere
  // else today; take the access token the same way the billing screens do
  // (supabase.auth.getSession() -> session.access_token) rather than
  // introducing a second auth-token convention (open item, resolved here).
  async function getAccessToken() {
    if (!supabase) return null;
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token ?? null;
  }

  // Lazy — only relevant for the paused + support case, fetched when the
  // panel first renders rather than folded into the modal's initial
  // Promise.all above (that already pays for three round-trips).
  // advertiser_payments already carries the "Internal support can select all
  // payments" SELECT policy (migration 058), so this is a plain client read.
  const fetchFailedAttempts = async () => {
    if (!supabase || advertiserId == null) return;
    setFailedAttemptsError(null);
    const lookbackStart = failedAttemptLookbackStart(autoChargePausedInfo?.paused_at);
    const { data, error } = await supabase
      .from("advertiser_payments")
      .select("id, charge_date, charge_period_start, amount")
      .eq("advertiser_id", advertiserId)
      .eq("source", "automatic")
      .in("status", ["failed", "skipped_no_card"])
      .gte("charge_date", lookbackStart)
      .order("charge_date", { ascending: false });
    if (error) {
      // Leave failedAttempts as null so the effect's own `=== null` guard
      // still holds and the Retry button below can genuinely re-run this.
      setFailedAttemptsError(error.message || "Failed to load charge attempts.");
      return;
    }
    const rows = data ?? [];
    setFailedAttempts(rows);
    // Newest first, newest preselected (D20) — the latest attempt's window
    // spans every earlier one, since the watermark does not advance on
    // failure.
    setSelectedFailedPaymentId(rows[0]?.id ?? "");
  };

  useEffect(() => {
    if (autoChargePausedInfo && isSupportUser && failedAttempts === null) {
      fetchFailedAttempts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoChargePausedInfo, isSupportUser]);

  const handleRecordOfflinePayment = async () => {
    if (!selectedFailedPaymentId) {
      setRecordError("Select which charge attempt this payment covers.");
      return;
    }
    const parsedAmount = parseFloat(offlinePaymentAmount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setRecordError("Enter an amount greater than $0.");
      return;
    }
    if (!offlinePaymentNote.trim()) {
      setRecordError("A reference/note is required (e.g. a wire or bank reference).");
      return;
    }
    setRecording(true);
    setRecordError(null);
    setRecordResult(null);
    try {
      const authToken = await getAccessToken();
      if (!authToken) throw new Error("No active session — reload and try again.");
      const result = await recordOfflinePayment(advertiserId, authToken, {
        failedPaymentId: Number(selectedFailedPaymentId),
        amount: parsedAmount,
        note: offlinePaymentNote.trim(),
      });
      setRecordResult(result);
      setOfflinePaymentAmount("");
      setOfflinePaymentNote("");
      await fetchFailedAttempts();
    } catch (err) {
      setRecordError(err?.message || "Failed to record the offline payment.");
    } finally {
      setRecording(false);
    }
  };

  const handleResolveBillingPause = async () => {
    setResolving(true);
    setResolveError(null);
    try {
      const authToken = await getAccessToken();
      if (!authToken) throw new Error("No active session — reload and try again.");
      await resolveBillingPause(advertiserId, authToken);
      setAutoChargePausedInfo(null);
    } catch (err) {
      setResolveError(err?.message || "Failed to resolve the billing pause.");
    } finally {
      setResolving(false);
    }
  };

  const handleSave = async () => {
    if (!supabase || advertiserId == null || !canManageAdvertiserData) return;
    setSaving(true);
    setSaveError(null);
    const bail = (msg) => { setSaveError(msg); };

    try {
      const parsedBudgetLimit = budgetLimit.trim() ? parseInt(budgetLimit, 10) : null;

      if (isSupportUser) {
        const parsedTargetMargin = parseFloat(targetMargin);
        if (isNaN(parsedTargetMargin) || parsedTargetMargin < 0 || parsedTargetMargin > 100) {
          bail("Target margin must be a number between 0% and 100%.");
          return;
        }
      }

      let warning = null; // set when reactivation is skipped because MTD spend is at/above the cap
      const needsPrerequisiteCheck =
        (COLD_STATUSES.includes(initialStatus.current) && !COLD_STATUSES.includes(status)) ||
        (initialStatus.current === "paused" && status === "active");

      if (needsPrerequisiteCheck) {
        const monthYear = getCurrentMonthFirstDay();
        const [creativesRes, settingsRes] = await Promise.all([
          checkActiveCreative(advertiserId),
          supabase
            .from("campaign_settings")
            .select("id, spend_cap")
            .eq("advertiser_id", advertiserId)
            .eq("month_year", monthYear)
            .in("status", ["approved", "pending"])
            .order("updated_at", { ascending: false })
            .limit(1)
            .maybeSingle(),
        ]);
        if (creativesRes.error || settingsRes.error) {
          bail(`Failed to verify activation prerequisites: ${creativesRes.error?.message || settingsRes.error?.message}`);
          return;
        }
        const errors = [];
        if (!creativesRes.hasActiveCreative) errors.push("no active creatives with an active variation");
        if (!settingsRes.data) errors.push(`no approved/pending campaign settings for ${monthYear}`);
        if (errors.length) { bail(`Cannot change status: ${errors.join(" and ")}.`); return; }

        if (status === "active") {
          const { spendSum, error: spendError } = await fetchMTDSpend(advertiserId);
          if (spendError) { bail(`Failed to verify activation prerequisites: ${spendError.message}`); return; }
          const cap = settingsRes.data?.spend_cap ?? null;
          if (isAtOrAboveCapThreshold(spendSum, cap)) {
            const pct = Math.round((spendSum / cap) * 100);
            warning = `Campaigns were not started for this advertiser: MTD spend ($${spendSum.toLocaleString()}) is already at ${pct}% of the $${cap.toLocaleString()} spend cap.`;
          }
        }
      }

      const updatePayload = {
        enabled_geo_types: enabledGeoTypes,
        status,
        budget_limit: Number.isFinite(parsedBudgetLimit) ? parsedBudgetLimit : null,
        ...(isSupportUser ? { target_margin: parseFloat(targetMargin) / 100 } : {}),
      };

      const { data, error } = await supabase
        .from("advertisers")
        .update(updatePayload)
        .eq("id", advertiserId)
        .select("id")
        .maybeSingle();
      if (error || !data) { bail(error?.message || "Advertiser settings were not saved."); return; }

      const from = initialStatus.current;
      const to = status;
      initialStatus.current = to;

      if (from !== to && to === "active") {
        if (!warning) {
          if (COLD_STATUSES.includes(from)) {
            const thresholdError = await ensureAttributionThreshold(advertiserId);
            if (thresholdError) { bail(`Settings saved, but ${thresholdError}`); return; }
          }

          const { error: jobError } = await dispatchActivationJobs({ advertiserId });
          if (jobError) { bail(`Settings saved, but failed to trigger activation jobs: ${jobError.message}`); return; }
        }

      } else if (from !== to && to === "paused") {
        const { error: pauseError } = await setCampaignStatus({ advertiserId, status: false });
        if (pauseError) { bail(`Settings saved, but failed to trigger pause job: ${pauseError.message}`); return; }
      }

      // advertiser_auto_charge_settings is its own table (not a column on
      // advertisers), so it needs its own write. Only relevant for
      // credit_card billing — upsert since the row may not exist yet.
      if (billingMethod === "credit_card") {
        if (autoChargeLoadFailed) {
          bail("Auto-charge settings could not be loaded, so they were not saved. Reopen this modal and try again.");
          return;
        }
        const { error: autoChargeError } = await supabase
          .from("advertiser_auto_charge_settings")
          .upsert({ advertiser_id: advertiserId, enabled: autoChargeEnabled }, { onConflict: "advertiser_id" });
        if (autoChargeError) { bail(`Settings saved, but failed to update auto-charge: ${autoChargeError.message}`); return; }
      }

      onSaved?.(warning);
      requestClose();
    } catch (err) {
      setSaveError(err?.message || "An unexpected error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell motion={motion} labelledBy="advertiser-settings-title" maxWidth={420} panelStyle={{ overflow:"visible", display:"flex", flexDirection:"column" }}>
        <div style={{ padding:"28px 28px 0", flexShrink:0 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <h4 id="advertiser-settings-title" style={{ fontSize:16, fontWeight:600, color:"#151515", margin:0, fontFamily:"'Clash Display Variable','Clash Display',sans-serif" }}>Advertiser Settings</h4>
            <button onClick={requestClose} aria-label="Close" style={{ background:"none", border:"none", color:"#626262", fontSize:20, cursor:"pointer" }}>✕</button>
          </div>
          <div style={{ fontSize:12, color:"#7b7b7b", marginBottom:20 }}>
            {advertiserName || `Advertiser #${advertiserId}`}
          </div>
        </div>

        <div style={{ padding:"0 28px 20px", overflowY:"auto", flex:"1 1 auto", minHeight:0 }}>

        {loading && <div style={{ padding:16, textAlign:"center", color:"#7b7b7b", fontSize:13 }}>Loading…</div>}

        {!loading && loadError && (
          <div style={{ padding:"10px 12px", marginBottom:16, background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:13 }}>
            Failed to load settings: {loadError}
          </div>
        )}

        {!loading && !loadError && pendingRow && (
          <div style={{ marginBottom:20, background:"#FFF5FB", border:"1px solid #F9009133", borderRadius:12, padding:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8, marginBottom:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontWeight:600, color:"#151515", fontSize:13 }}>Pending Changes</span>
                <PendingChip />
              </div>
              <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                <button
                  onClick={handleReject}
                  disabled={pendingActioning}
                  style={{ padding:"5px 14px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:12, fontWeight:500, cursor: pendingActioning ? "not-allowed" : "pointer" }}
                >Reject</button>
                <button
                  onClick={handleApprove}
                  disabled={pendingActioning}
                  style={{ padding:"5px 14px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor: pendingActioning ? "not-allowed" : "pointer", opacity: pendingActioning ? 0.7 : 1 }}
                >Approve</button>
              </div>
            </div>
            {pendingActionError && (
              <div style={{ padding:"8px 10px", marginBottom:10, background:"#FFF0F0", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:12 }}>{pendingActionError}</div>
            )}
            {pendingDiffs.length === 0 ? (
              <div style={{ fontSize:12, color:"#626262" }}>
                No field differences — this proposal matches the current settings.
              </div>
            ) : pendingDiffs.map(f => {
              const isCleared = pendingClearedFields.includes(f.key);
              return (
                <div key={f.key} style={{ marginBottom:10, minWidth:0 }}>
                  <div style={{ fontSize:10, color:"#626262", textTransform:"uppercase", letterSpacing:1, marginBottom:3 }}>{f.label}</div>
                  {/* Old and new stack on their own lines. A single wrapping flex row
                      orphaned the arrow at the end of the old value whenever the text
                      was long enough to wrap (URLs, brand copy). Leading the new value
                      with the arrow keeps the two sides readable at any length. */}
                  <div style={{ fontSize:12, color:"#B8B8B8", textDecoration:"line-through", overflowWrap:"anywhere", minWidth:0 }}>
                    {advertiserBaseline?.[f.key] ?? "—"}
                  </div>
                  <div style={{ display:"flex", gap:6, fontSize:12, minWidth:0, marginTop:1 }}>
                    <span style={{ color:"#151515", flexShrink:0 }} aria-hidden="true">→</span>
                    <span style={{ color:"#F90091", fontWeight:600, overflowWrap:"anywhere", minWidth:0 }}>{isCleared ? "—" : pendingRow[f.key]}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !loadError && (
          <>
            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Credit Limit</div>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ display:"flex", alignItems:"center", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"8px 12px", width:"100%" }}>
                  <span style={{ color:"#7b7b7b", fontSize:13 }}>$</span>
                  <input
                    value={budgetLimit}
                    onChange={(e) => setBudgetLimit(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="No limit"
                    style={{ background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:14, width:"100%", marginLeft:4 }}
                  />
                </div>
              </div>
            </div>

            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Internal UI</div>
              <button
                type="button"
                onClick={() => {
                  if (!legacyCampaignSettingsUrl) return;
                  window.open(legacyCampaignSettingsUrl, "_blank", "noopener,noreferrer");
                }}
                disabled={!legacyCampaignSettingsUrl}
                style={{ padding:"9px 12px", background:"#FFF5FB", color:"#F90091", border:"1px solid #F9009133", borderRadius:8, fontSize:13, fontWeight:600, cursor: legacyCampaignSettingsUrl ? "pointer" : "not-allowed", opacity: legacyCampaignSettingsUrl ? 1 : 0.5 }}
              >
                Internal Campaigns Settings
              </button>
            </div>

            <div style={{ marginBottom:20, display:"flex", gap:12 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Advertiser Status</div>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={!canManageAdvertiserData}
                  style={{ width:"100%", padding:"9px 32px 9px 12px", background:`#FAFAFA url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%23626262' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'><path d='M1 1.5L6 6.5L11 1.5'/></svg>") no-repeat right 12px center / 10px 6px`, border:"1px solid #E2E2E2", borderRadius:8, color:"#151515", fontSize:13, fontFamily:"inherit", outline:"none", appearance:"none", WebkitAppearance:"none", MozAppearance:"none", cursor: canManageAdvertiserData ? "pointer" : "not-allowed", opacity: canManageAdvertiserData ? 1 : 0.65 }}
                >
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="canceled">Canceled</option>
                  {/* `onboarding` is the creation default and is not selectable here. */}
                  {initialStatus.current === "onboarding" && <option value="onboarding">Onboarding</option>}
                </select>
              </div>

              {isSupportUser && (
                <div style={{ flex:"0 0 110px" }}>
                  <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>Target Margin</div>
                  <div style={{ display:"flex", alignItems:"center", background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8, padding:"8px 12px", height:"100%", boxSizing:"border-box", maxHeight:38 }}>
                    <input
                      value={targetMargin}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (/^(\d*\.?\d*)$/.test(v)) setTargetMargin(v);
                      }}
                      placeholder="80"
                      aria-label="Target margin percentage"
                      style={{ background:"transparent", border:"none", outline:"none", color:"#151515", fontSize:14, width:"100%", minWidth:0 }}
                    />
                    <span style={{ color:"#7b7b7b", fontSize:13, flexShrink:0 }}>%</span>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom:20 }}>
              <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:12 }}>Geo Targeting Features</div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {KNOWN_GEO_TYPES.map(type => {
                  const selected = enabledGeoTypes.includes(type.value);
                  return (
                    <button
                      type="button"
                      key={type.value}
                      onClick={() => canManageAdvertiserData && toggleGeoType(type.value)}
                      disabled={!canManageAdvertiserData}
                      aria-pressed={selected}
                      style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", background: selected ? "#FFF5FB" : "#FAFAFA", borderRadius:8, cursor: canManageAdvertiserData ? "pointer" : "not-allowed", border:`1px solid ${selected ? "#F90091" : "#E2E2E2"}`, opacity: canManageAdvertiserData ? 1 : 0.65 }}>
                      <div style={{ width:16, height:16, borderRadius:4, background: selected ? "#F90091" : "#E2E2E2", border:`1px solid ${selected ? "#F90091" : "#CFCFCF"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                        {selected && <span style={{ color:"#fff", fontSize:10, lineHeight:1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize:13, color: selected ? "#151515" : "#626262" }}>{type.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {billingMethod === "credit_card" && (
              <div style={{ marginBottom:20 }}>
                <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:12 }}>Conversions auto-charging</div>
                <button
                  type="button"
                  onClick={() => canManageAdvertiserData && setAutoChargeEnabled(v => !v)}
                  disabled={!canManageAdvertiserData}
                  aria-pressed={autoChargeEnabled}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", width:"100%", boxSizing:"border-box", background: autoChargeEnabled ? "#FFF5FB" : "#FAFAFA", borderRadius:8, cursor: canManageAdvertiserData ? "pointer" : "not-allowed", border:`1px solid ${autoChargeEnabled ? "#F90091" : "#E2E2E2"}`, opacity: canManageAdvertiserData ? 1 : 0.65 }}>
                  <div style={{ width:16, height:16, borderRadius:4, background: autoChargeEnabled ? "#F90091" : "#E2E2E2", border:`1px solid ${autoChargeEnabled ? "#F90091" : "#CFCFCF"}`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    {autoChargeEnabled && <span style={{ color:"#fff", fontSize:10, lineHeight:1 }}>✓</span>}
                  </div>
                  <span style={{ fontSize:13, color: autoChargeEnabled ? "#151515" : "#626262" }}>Auto-charge daily conversions</span>
                </button>
                {autoChargePausedInfo && (
                  <div style={{ marginTop:10, padding:10, background:"#FFF5FB", border:"1px solid #FFE9F6", borderRadius:8, fontSize:12, color:"#3E3E3E" }}>
                    Paused since {formatPausedAt(autoChargePausedInfo.paused_at)} after {autoChargePausedInfo.failure_count}{" "}
                    {autoChargePausedInfo.paused_reason === "no_card"
                      ? `${autoChargePausedInfo.failure_count === 1 ? "day" : "days"} with no card on file. Resumes automatically when a card is added.`
                      : `failed ${autoChargePausedInfo.failure_count === 1 ? "attempt" : "attempts"} on every card on file. Resumes automatically when a new card is added, or resolve it manually below.`}
                  </div>
                )}

                {/*
                  WU-5 (DEV-2116) — support-only billing-pause resolution.
                  R1 (load-bearing ordering, do not reorder): the "record an
                  offline payment" sub-form renders ABOVE the "resolve
                  billing pause" button, and the button's own helper text
                  repeats the same instruction. Record the offline payment
                  FIRST, resolve the pause SECOND — resolving first re-opens
                  the account to a charge run that would still see those
                  conversions as unpaid. The two actions are deliberately
                  independent (D14) and this ordering is not enforced in
                  code — only in this layout and copy, so a reviewer diffing
                  this component must confirm both survive a future refactor.
                */}
                {autoChargePausedInfo && isSupportUser && (
                  <div style={{ marginTop:10, padding:12, background:"#FAFAFA", border:"1px solid #E2E2E2", borderRadius:8 }}>
                    <div style={{ fontSize:11, color:"#B8B8B8", textTransform:"uppercase", letterSpacing:1, marginBottom:8 }}>
                      Support: resolve billing pause
                    </div>

                    {/*
                      Wrapped in a block-level div on purpose: <button> is
                      inline-block, so with the sub-form collapsed this
                      disclosure and the "Resolve billing pause" button below
                      are adjacent inline elements and render on the SAME line,
                      overlapping. The wrapper forces the line break that keeps
                      R1's ordering visible rather than collided.
                    */}
                    <div style={{ marginBottom:8 }}>
                      <button
                        type="button"
                        onClick={() => setShowOfflinePaymentForm((v) => !v)}
                        style={{ padding:"6px 0", background:"none", border:"none", color:"#F90091", fontSize:12, fontWeight:600, cursor:"pointer" }}
                      >
                        {showOfflinePaymentForm ? "▾" : "▸"} Record an offline payment
                      </button>
                    </div>

                    {showOfflinePaymentForm && (
                      <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:12 }}>
                        {failedAttemptsError && (
                          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                            <span style={{ fontSize:12, color:"#e53935" }}>{failedAttemptsError}</span>
                            {/*
                              Without this the panel is a dead end: the fetch
                              effect only re-runs while failedAttempts is
                              still null and its deps never change again, so
                              a single transient failure left an empty,
                              permanently disabled dropdown until remount.
                            */}
                            <button
                              type="button"
                              onClick={fetchFailedAttempts}
                              style={{ padding:"4px 10px", background:"#FFFFFF", color:"#151515", border:"1px solid #E2E2E2", borderRadius:8, fontSize:12, cursor:"pointer" }}
                            >
                              Retry
                            </button>
                          </div>
                        )}
                        {!failedAttemptsError && failedAttempts?.length === 0 && (
                          // An empty result is a real, explainable state — not
                          // the same thing as a load failure, and never a
                          // silent disabled button with no dropdown options.
                          <div style={{ fontSize:12, color:"#3E3E3E" }}>
                            No failed charge attempts in the last {FAILED_ATTEMPT_LOOKBACK_DAYS} days, so there is
                            nothing to record a payment against. Resolve the pause below if the card issue is fixed.
                          </div>
                        )}
                        {failedAttempts?.length > 0 && (
                          <>
                            <label style={{ fontSize:11, color:"#626262" }}>
                              Which charge attempt did this cover?
                              <select
                                value={selectedFailedPaymentId}
                                onChange={(e) => setSelectedFailedPaymentId(e.target.value)}
                                disabled={recording}
                                style={{ display:"block", width:"100%", boxSizing:"border-box", marginTop:4, padding:"8px 10px", paddingRight:32, background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, color:"#151515" }}
                              >
                                {failedAttempts.map((row) => (
                                  <option key={row.id} value={row.id}>
                                    {row.charge_date} — {amountLabel(row.amount)} (covers {row.charge_period_start ?? row.charge_date}..{row.charge_date})
                                  </option>
                                ))}
                              </select>
                            </label>
                            <div style={{ fontSize:11, color:"#7b7b7b" }}>
                              These attempts retry the same unpaid window, so the most recent one covers the full outstanding balance. Select the attempt finance collected against. The covered window ends when that attempt actually ran, so anything billed later stays chargeable.
                            </div>
                          </>
                        )}
                        <label style={{ fontSize:11, color:"#626262" }}>
                          Amount received
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={offlinePaymentAmount}
                            onChange={(e) => setOfflinePaymentAmount(e.target.value)}
                            placeholder="0.00"
                            disabled={recording}
                            style={{ display:"block", width:"100%", boxSizing:"border-box", marginTop:4, padding:"8px 10px", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, color:"#151515" }}
                          />
                        </label>
                        <div style={{ fontSize:11, color:"#7b7b7b" }}>May differ from our computed total — enter what finance received.</div>
                        <label style={{ fontSize:11, color:"#626262" }}>
                          Reference / note (required)
                          <input
                            value={offlinePaymentNote}
                            onChange={(e) => setOfflinePaymentNote(e.target.value)}
                            placeholder="e.g. wire ref #12345"
                            disabled={recording}
                            style={{ display:"block", width:"100%", boxSizing:"border-box", marginTop:4, padding:"8px 10px", background:"#FFFFFF", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, color:"#151515" }}
                          />
                        </label>
                        {recordError && (
                          <div style={{ fontSize:12, color:"#e53935" }}>{recordError}</div>
                        )}
                        {recordResult && (
                          <div style={{ fontSize:12, color:"#1E7A34" }}>
                            Recorded {amountLabel(recordResult.amount_recorded)} against {recordResult.linked_count} conversion{recordResult.linked_count === 1 ? "" : "s"} totalling {amountLabel(recordResult.linked_spend_total)}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={handleRecordOfflinePayment}
                          disabled={recording || !selectedFailedPaymentId}
                          style={{ padding:"8px 0", background:"#151515", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor: recording ? "not-allowed" : "pointer", opacity: recording ? 0.7 : 1 }}
                        >
                          {recording ? "Recording…" : "Record payment"}
                        </button>
                      </div>
                    )}

                    {resolveError && (
                      <div style={{ fontSize:12, color:"#e53935", marginBottom:8 }}>{resolveError}</div>
                    )}
                    <button
                      type="button"
                      onClick={handleResolveBillingPause}
                      disabled={resolving}
                      style={{ padding:"8px 12px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:12, fontWeight:600, cursor: resolving ? "not-allowed" : "pointer", opacity: resolving ? 0.7 : 1 }}
                    >
                      {resolving ? "Resolving…" : "Resolve billing pause"}
                    </button>
                    <div style={{ fontSize:11, color:"#7b7b7b", marginTop:6 }}>
                      Record any offline payment above before resolving — resolving first lets the next automatic charge run against still-unpaid conversions.
                    </div>
                  </div>
                )}
              </div>
            )}

            {isSupportUser && <AdvertiserCreditsSection advertiserId={advertiserId} />}

            {saveError && (
              <div style={{ padding:"10px 12px", background:"#FFF5F5", border:"1px solid #fca5a5", borderRadius:8, color:"#e53935", fontSize:13 }}>{saveError}</div>
            )}
          </>
        )}
        </div>

        {!loading && (
          <div style={{ padding:"16px 28px", borderTop:"1px solid #E2E2E2", display:"flex", gap:10, justifyContent:"flex-end", flexShrink:0 }}>
            {loadError ? (
              <button onClick={requestClose} style={{ padding:"9px 20px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer" }}>Close</button>
            ) : (
              <>
                <button onClick={requestClose} style={{ padding:"9px 20px", background:"#FAFAFA", color:"#626262", border:"1px solid #E2E2E2", borderRadius:8, fontSize:13, fontWeight:500, cursor:"pointer" }}>Cancel</button>
                <button onClick={handleSave} disabled={saving || !canManageAdvertiserData} style={{ padding:"9px 20px", background:"#F90091", color:"#fff", border:"none", borderRadius:8, fontSize:13, fontWeight:600, cursor: saving || !canManageAdvertiserData ? "not-allowed" : "pointer", opacity: saving || !canManageAdvertiserData ? 0.7 : 1 }}>
                  {saving ? "Saving…" : "Save"}
                </button>
              </>
            )}
          </div>
        )}
    </ModalShell>
  );
}
